import { Label } from '@/components/ui/label';
import { LocationSelector } from '@/components/analytics/LocationSelector';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart, MapPin, Smartphone, Calendar, Clock, Rss, RefreshCw } from 'lucide-react';
import { subDays } from 'date-fns';

export interface AnalyticsFiltersProps {
  // Location filter props
  locations: Array<{ id: string; name: string; subLocations?: any[] }>;
  selectedLocation: string;
  onLocationChange: (locationId: string) => void;

  // Device filter props
  devices: Array<{ id: string; name?: string | null; serial_number: string }>;
  selectedDevice: string;
  onDeviceChange: (deviceId: string) => void;

  // Date range filter props
  dateRange: { from: Date; to: Date };
  onDateRangeChange: (range: { from: Date; to: Date }) => void;

  // Interval filter props
  interval: string;
  onIntervalChange: (interval: string) => void;

  // Live data toggle props
  liveData: boolean;
  onLiveDataToggle: (enabled: boolean) => void;

  // Refresh props
  isRefreshing: boolean;
  onRefresh: () => void;

  // Optional className for styling
  className?: string;
}

/**
 * Enterprise-grade analytics filters component
 * Provides a responsive, modular filter system for analytics dashboard
 */
export function AnalyticsFilters({
  locations,
  selectedLocation,
  onLocationChange,
  devices,
  selectedDevice,
  onDeviceChange,
  dateRange,
  onDateRangeChange,
  interval,
  onIntervalChange,
  liveData,
  onLiveDataToggle,
  isRefreshing,
  onRefresh,
  className = '',
}: AnalyticsFiltersProps) {
  const handleDeviceChange = (value: string) => {
    onDeviceChange(value === 'all' ? '' : value);
  };

  const handleFromDateChange = (date: Date | undefined) => {
    if (date) {
      onDateRangeChange({ ...dateRange, from: date });
    }
  };

  const handleToDateChange = (date: Date | undefined) => {
    if (date) {
      onDateRangeChange({ ...dateRange, to: date });
    }
  };

  const handleQuickRange = (days: number) => {
    const now = new Date();
    onDateRangeChange({
      from: subDays(now, days),
      to: now,
    });
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Filters Section Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <BarChart className="h-4 w-4" />
        Filters & Controls
      </div>

      {/* Main Filters Grid - Responsive Layout */}
      <div className="space-y-4">
        {/* Primary Filters Row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Location Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Location
            </Label>
            <LocationSelector locations={locations} onLocationChange={onLocationChange} value={selectedLocation} />
          </div>

          {/* Device Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Smartphone className="h-3 w-3" />
              Device
            </Label>
            <Select value={selectedDevice || 'all'} onValueChange={handleDeviceChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select Device" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Devices</SelectItem>
                {devices?.map((device) => (
                  <SelectItem key={device.id} value={device.id}>
                    {device.name || device.serial_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Interval Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Clock className="h-3 w-3" />
              Interval
            </Label>
            <Select value={interval} onValueChange={onIntervalChange}>
              <SelectTrigger>
                <SelectValue placeholder="Interval" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minute">Per Minute</SelectItem>
                <SelectItem value="hour">Hourly</SelectItem>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quick Actions Filter */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">Quick Actions</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleQuickRange(1)} className="text-xs flex-1">
                24h
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleQuickRange(7)} className="text-xs flex-1">
                7d
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleQuickRange(30)} className="text-xs flex-1">
                30d
              </Button>
            </div>
          </div>
        </div>

        {/* Date Range Filter - Full Width for Better UX */}
        <div className="grid grid-cols-1">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Calendar className="h-3 w-3" />
              Date Range
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <div className="flex-1">
                <DateTimePicker
                  value={dateRange.from}
                  onChange={handleFromDateChange}
                  onMonthChange={() => undefined}
                  hourCycle={12}
                  granularity="minute"
                  placeholder="Start date"
                />
              </div>
              <span className="text-xs text-muted-foreground self-center">to</span>
              <div className="flex-1">
                <DateTimePicker
                  value={dateRange.to}
                  onChange={handleToDateChange}
                  onMonthChange={() => undefined}
                  hourCycle={12}
                  granularity="minute"
                  placeholder="End date"
                />
              </div>
            </div>
          </div>
        </div>

        <Separator className="opacity-50" />

        {/* Control Actions Row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Switch id="live-data" checked={liveData} onCheckedChange={onLiveDataToggle} />
            <Label htmlFor="live-data" className="flex items-center gap-1.5 text-sm font-medium cursor-pointer">
              <Rss className="h-4 w-4" />
              Live Data
            </Label>
          </div>

          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>
    </div>
  );
}
